// ================ Action types ================ //

import { onDeleteSharetribeUser, onDeleteUser, upsertUserDetailsApi } from '../../util/api';
import { storableError } from '../../util/errors';

export const DELETE_ACCOUNT_REQUEST = 'app/DeleteAccountPage/DELETE_ACCOUNT_REQUEST';
export const DELETE_ACCOUNT_SUCCESS = 'app/DeleteAccountPage/DELETE_ACCOUNT_SUCCESS';
export const DELETE_ACCOUNT_ERROR = 'app/DeleteAccountPage/DELETE_ACCOUNT_ERROR';

// ================ Reducer ================ //

const initialState = {
  deleteInProgress: false,
  deleteError: null,
  deleteSuccess: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case DELETE_ACCOUNT_REQUEST:
      return { ...state, deleteInProgress: true, deleteError: null, deleteSuccess: false };
    case DELETE_ACCOUNT_SUCCESS:
      return { ...state, deleteInProgress: false, deleteError: null, deleteSuccess: true };
    case DELETE_ACCOUNT_ERROR:
      return { ...state, deleteInProgress: false, deleteError: payload, deleteSuccess: false };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const deleteAccountRequest = () => ({ type: DELETE_ACCOUNT_REQUEST });
export const deleteAccountSuccess = () => ({ type: DELETE_ACCOUNT_SUCCESS });
export const deleteAccountError = e => ({ type: DELETE_ACCOUNT_ERROR, payload: e, error: true });

// ================ Thunks ================ //

// NOTE: Wire real API call here when backend is ready.
// For example: call an endpoint like `/api/delete-account` that triggers user deletion.
export const deleteAccount = (password) => async (dispatch, getState, sdk) => {
  try {
    // 🔹 Dispatch request start
    dispatch(deleteAccountRequest());

    // 🔹 Ensure user is authenticated
    const state = getState();
    const currentUser = state?.user?.currentUser;
    if (!currentUser) {
      throw new Error("Not authenticated");
    }

    // 🔹 Step 1: Check for pending transactions or wallet activity
    const deleteUserResponse = await onDeleteUser({ userId: currentUser.id.uuid });

    const hasPendingTransactions = deleteUserResponse?.pendingTransactions?.length > 0;
    const hasPendingWalletTransactions = deleteUserResponse?.pendingWalletTransactions?.length > 0;

    if (hasPendingTransactions || hasPendingWalletTransactions) {
      // ❌ Block deletion due to pending activity
      dispatch(
        deleteAccountError({
          message: "User has pending transactions or wallet activities",
          pendingTransactions: deleteUserResponse.pendingTransactions || [],
          pendingWalletTransactions: deleteUserResponse.pendingWalletTransactions || [],
        })
      );

      return Promise.reject(new Error("User has pending transactions or wallet activities"));
    }

    // 🔹 Step 2: Disable SMS notifications in MongoDB before deletion
    try {
      await upsertUserDetailsApi({ 
        userId: currentUser.id.uuid, 
        smsNotificationsEnabled: false 
      });
    } catch (err) {
      console.error('Failed to disable SMS notifications:', err);
      // Continue with deletion even if this fails
    }

    // 🔹 Step 3: Delete user in Flex (requires password)
    try {
      await onDeleteSharetribeUser({ password });

      // ✅ Dispatch success if deletion succeeded
      dispatch(deleteAccountSuccess());
      return { success: true };
    } catch (err) {
      // ❌ Wrong password or API error
      dispatch(deleteAccountError(storableError(err)));
      throw new Error("Incorrect password");
    }

  } catch (err) {
    // 🔹 Catch unexpected errors
    console.error("deleteAccount failed:", err);
    dispatch(deleteAccountError(storableError(err)));
    throw err; // 🔥 Re-throw so UI `catch` works
  }
};
